/**
 * EDITORIAL template — narrative scroll through a 3D environment.
 *
 * Single long-scroll section. Camera flies forward along the Z axis through fog.
 * Products are scene objects spaced along the path. As the camera passes each,
 * a floating caption fades in beside it. Atmospheric, moody, premium.
 *
 * Best for: luxury, narrative brands, single-line collections shown editorially.
 */
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { buildMesh, setupEnvironment, retintMesh, TONEMAPS } from '../../core/scene-utils.js';
import { el, createDisposer } from '../../core/utils.js';
import { editorialLayout } from './layout.js';

const SECTION_LENGTH = 9;    // world units between products along Z
const CAM_DIST = 4.5;        // how far the camera sits in front of the framed product

export function mount(canvas, products, look, host, opts = {}) {
  const cfg = {
    fov: 55, exposure: 0.95, tonemap: 'aces',
    fogColor: '#0a0a14', fogDensity: 0.045,
    rimLightColor: '#bf00ff', rimLightIntensity: 1.6,
    keyLightColor: '#ffffff', keyLightIntensity: 0.5,
    envIntensity: 0.55, bloomStrength: 0.9, bloomRadius: 0.7, bloomThreshold: 0.4,
    hdriUrl: null,
    ...look,
  };

  const disposer = createDisposer();
  const dom = editorialLayout(host, { embedded: !!opts.embedded });

  // ---- Renderer ----
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = TONEMAPS[cfg.tonemap] ?? THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = cfg.exposure;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(cfg.fogColor);
  scene.fog = new THREE.FogExp2(cfg.fogColor, cfg.fogDensity);

  const camera = new THREE.PerspectiveCamera(cfg.fov, 1, 0.1, 200);
  camera.position.set(0, 0.6, 5);

  disposer.add(setupEnvironment(renderer, scene, cfg));

  scene.add(new THREE.AmbientLight(0xffffff, 0.18));
  const key = new THREE.DirectionalLight(cfg.keyLightColor, cfg.keyLightIntensity);
  key.position.set(2, 4, 3);
  scene.add(key);
  const rim = new THREE.PointLight(cfg.rimLightColor, cfg.rimLightIntensity, 18);
  rim.position.set(-2, 1, 0);
  scene.add(rim);

  // Floor — long strip vanishing into fog.
  const floorGeo = new THREE.PlaneGeometry(40, products.length * SECTION_LENGTH + 40);
  const floorMat = new THREE.MeshStandardMaterial({
    color: '#15151c', roughness: 0.85, metalness: 0.05,
  });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -1.2;
  floor.position.z = -(products.length * SECTION_LENGTH) / 2;
  scene.add(floor);

  // Subtle particle dust (parallax / atmosphere).
  const PARTICLES = 220;
  const dustGeom = new THREE.BufferGeometry();
  const positions = new Float32Array(PARTICLES * 3);
  for (let i = 0; i < PARTICLES; i++) {
    positions[i * 3 + 0] = (Math.random() - 0.5) * 16;
    positions[i * 3 + 1] = Math.random() * 6 - 2;
    positions[i * 3 + 2] = Math.random() * (CAM_DIST + 12) - 12;  // around the stage
  }
  dustGeom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const dustMat = new THREE.PointsMaterial({
    color: '#ffffff', size: 0.04, transparent: true, opacity: 0.45, depthWrite: false,
  });
  const dust = new THREE.Points(dustGeom, dustMat);
  scene.add(dust);

  // Center-stage presentation: every product lives at the origin, auto-fit to a
  // consistent size. Only the active product is shown; others scale to zero.
  const TARGET_SIZE = 2.0;
  const meshes = products.map((p, i) => {
    const m = buildMesh(p);
    // Primitives: auto-fit the group now (geometry is synchronous).
    // GLB models: loadModel sizes the async content itself via modelScale,
    // so leave the group at 1 to avoid double-scaling.
    let fit = 1;
    if (!p.model) {
      const box = new THREE.Box3().setFromObject(m);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      fit = TARGET_SIZE / maxDim;
    }
    m.userData.fit = fit;
    m.userData.s = i === 0 ? fit : 0;   // animated current scale
    m.userData.product = p;
    m.scale.setScalar(m.userData.s);
    m.visible = i === 0;
    scene.add(m);
    return m;
  });

  // ---- Postprocessing ----
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    cfg.bloomStrength, cfg.bloomRadius, cfg.bloomThreshold,
  );
  composer.addPass(bloom);
  composer.addPass(new OutputPass());

  // ---- State ----
  const state = { targetIndex: 0 };
  function setActive(i) {
    state.targetIndex = Math.max(0, Math.min(products.length - 1, i));
    dom.setCaption(state.targetIndex);
  }
  function retintActive(colors) {
    const m = meshes[state.targetIndex];
    if (!m) return;
    retintMesh(m, colors);
    const p = m.userData.product;
    if (p) Object.assign(p, colors);
  }

  let paused = false;
  function setPaused(v) { paused = !!v; }

  // ---- Mouse parallax ----
  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  const onPointer = (e) => {
    mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.ty = -((e.clientY / window.innerHeight) * 2 - 1);
  };
  window.addEventListener('pointermove', onPointer, { passive: true });
  disposer.add(() => window.removeEventListener('pointermove', onPointer));

  // ---- Resize ----
  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener('resize', resize);
  disposer.add(() => window.removeEventListener('resize', resize));
  resize();

  // ---- Scroll: pin the stage, advance the active product by scroll progress ----
  const section = host.querySelector('.editorial-stage');
  const totalScrollLength = products.length * window.innerHeight * 0.85;
  const scrollTrigger = ScrollTrigger.create({
    trigger: section,
    start: 'top top',
    end: `+=${totalScrollLength}`,
    pin: true,
    scrub: 0.6,
    invalidateOnRefresh: true,
    onUpdate(self) {
      const span = Math.max(1, products.length - 1);
      setActive(Math.round(self.progress * span));
    },
  });
  disposer.add(() => scrollTrigger.kill());

  // ---- Tick ----
  rim.position.set(-2.2, 1.2, 1.5);   // fixed key rim on the stage
  let rafId = 0;
  function tick(t) {
    mouse.x += (mouse.tx - mouse.x) * 0.05;
    mouse.y += (mouse.ty - mouse.y) * 0.05;

    // Camera fixed in front of the stage; only orbits slightly with the cursor.
    camera.position.set(mouse.x * 0.5, 0.2 + mouse.y * 0.25, CAM_DIST);
    camera.lookAt(0, 0, 0);

    meshes.forEach((m, i) => {
      const isActive = i === state.targetIndex;
      const target = isActive ? m.userData.fit : 0;
      m.userData.s += (target - m.userData.s) * 0.12;   // scale-in / scale-out
      m.visible = m.userData.s > 0.001;
      if (!m.visible) return;
      m.scale.setScalar(m.userData.s);
      if (isActive) {
        m.rotation.y += 0.004;                          // slow turntable
        m.position.y = Math.sin(t * 0.0012) * 0.06;     // gentle float
        const sp = m.userData.spinner;
        if (sp) sp.rotation.z = -t * 0.002;
      }
    });

    // Dust drifts gently toward the camera, recycling behind the stage.
    const pos = dust.geometry.attributes.position;
    for (let i = 0; i < PARTICLES; i++) {
      pos.array[i * 3 + 2] += 0.008;
      if (pos.array[i * 3 + 2] > CAM_DIST) pos.array[i * 3 + 2] = -12;
    }
    pos.needsUpdate = true;

    if (!paused) composer.render();
    rafId = requestAnimationFrame(tick);
  }
  rafId = requestAnimationFrame(tick);
  disposer.add(() => cancelAnimationFrame(rafId));

  function destroy() {
    disposer.run();
    meshes.forEach((m) => {
      m.traverse((n) => { if (n.isMesh) { n.geometry?.dispose?.(); n.material?.dispose?.(); } });
    });
    dustGeom.dispose(); dustMat.dispose();
    floorGeo.dispose(); floorMat.dispose();
    composer.dispose?.();
    renderer.dispose();
    dom.cleanup();
  }

  return { setActive, setPaused, retintActive, destroy };
}
